
import { List, ShoppingBag, UserCircle, X } from '@phosphor-icons/react';
import { Link } from '@tanstack/react-router';
import { useLocation } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { useCart } from '@/components/cart-provider';
import { ThemeToggle } from '@/components/theme-toggle';
import { Wordmark } from '@/components/wordmark';
import { cn } from '@/lib/cn';

const NAV = [
  { href: '/shop', label: 'Shop' },
  { href: '/custom', label: 'Custom order' },
  { href: '/orders', label: 'Orders' },
];

export function SiteHeader() {
  const pathname = useLocation().pathname;
  const { count, ready } = useCart();
  const [open, setOpen] = useState(false);

  useEffect(() => setOpen(false), [pathname]);

  const links = NAV;

  return (
    <header className="sticky top-0 z-40 border-b border-line bg-panel/85 backdrop-blur-md">
      {/* Nav stays on one line at desktop and the bar stays under 80px. */}
      <div className="mx-auto flex h-[68px] max-w-[1180px] items-center justify-between gap-4 px-[30px]">
        <Wordmark />

        <nav aria-label="Main" className="hidden items-center gap-1 md:flex">
          {links.map((item) => {
            const active =
              pathname === item.href || pathname.startsWith(`${item.href}/`);
            return (
              <Link
                key={item.href}
                to={item.href}
                aria-current={active ? 'page' : undefined}
                className={cn(
                  'rounded-[10px] px-3 py-2.5 text-[15px] font-semibold transition-colors hover:bg-raised',
                  active && 'bg-raised text-brand-ink',
                )}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-1">
          <ThemeToggle />
          <Link
            to="/account"
            className="hidden size-12 place-items-center rounded-[10px] transition-colors hover:bg-raised sm:grid"
            aria-label="Your account"
          >
            <UserCircle size={22} weight="bold" aria-hidden />
          </Link>
          <Link
            to="/cart"
            className="relative grid size-12 place-items-center rounded-[10px] transition-colors hover:bg-raised"
            aria-label={ready ? `Cart, ${count} items` : 'Cart'}
          >
            <ShoppingBag size={22} weight="bold" aria-hidden />
            {ready && count > 0 && (
              <span className="absolute right-1.5 top-1.5 grid min-w-[19px] place-items-center rounded-full border-2 border-panel bg-brand-strong px-1 text-[10.5px] font-extrabold text-on-brand">
                {count}
              </span>
            )}
          </Link>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="grid size-12 place-items-center rounded-[10px] transition-colors hover:bg-raised md:hidden"
            aria-expanded={open}
            aria-controls="mobile-nav"
            aria-label={open ? 'Close menu' : 'Open menu'}
          >
            {open ? <X size={22} weight="bold" /> : <List size={22} weight="bold" />}
          </button>
        </div>
      </div>

      {open && (
        <nav
          id="mobile-nav"
          aria-label="Main"
          className="border-t border-line bg-panel px-5 py-2 md:hidden"
        >
          {[...links, { href: '/account', label: 'Account' }].map((item) => (
            <Link
              key={item.href}
              to={item.href}
              className="block rounded-[10px] px-3 py-3.5 text-[15px] font-semibold hover:bg-raised"
            >
              {item.label}
            </Link>
          ))}
        </nav>
      )}
    </header>
  );
}
